import { v4 as uuidv4 } from "uuid";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment-timezone";
import { DateTime } from "luxon";

import { TeamType, TeamTemplateType, OneOnOneTemplateType } from "@teaming/common-enums";
import { walk } from "@teaming/common-util";

export { default as lang } from "./lang";

export const teamingTemplateIdMap = {
	[ TeamTemplateType.REGULARLY_SCHEDULED_TEAM_MEETING ]: "d7465ba0-8e2c-11ed-847d-acde48001122",
	[ TeamTemplateType.DAILY_STANDUP ]: "d7465bd2-8e2c-11ed-847d-acde48001122",
	[ TeamTemplateType.SET_TEAM_GOALS ]: "d7465be6-8e2c-11ed-847d-acde48001122",
	[ TeamTemplateType.TEAM_RETROSPECTIVE ]: "d7465bfa-8e2c-11ed-847d-acde48001122",
	[ TeamTemplateType.ALLSTATE_WEEKLY_TEAM_MEETING ]: "d7465c04-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.REGULAR_WEEKLY ]: "a2ff6580-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.SOLVE_PROBLEMS ]: "a2ff65b2-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.RESOLVE_CONFLICTS ]: "a2ff65c6-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.GET_FEEDBACK ]: "a2ff65da-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.GIVE_FEEDBACK ]: "a2ff65e4-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.REVIEW_PERFORMANCE ]: "a2ff65ee-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.SET_GOALS ]: "a2ff6602-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.PROGRESS_CHECK_IN ]: "a2ff660c-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.RECOGNIZE_AND_APPRECIATE ]: "a2ff6616-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.ROLE_ALIGNMENT ]: "a2ff6617-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.ENGAGEMENT_CHECK_IN ]: "a2ff6620-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.HYBRID_WORK_CHECK_IN ]: "a2ff662a-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.REMOTE_WORK_CHECK_IN ]: "a2ff663e-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.PROFESSIONAL_DEVELOPMENT ]: "a2ff6648-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.SKIP_LEVEL_MEETING ]: "a2ff6652-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.FIRST_ONE_ON_ONE ]: "a2ff665c-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.QUARTERLY_PERFORMANCE_REVIEW ]: "a2ff6666-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.ANNUAL_PERFORMANCE_REVIEW ]: "a2ff6667-8e2c-11ed-847d-acde48001122",
	[ OneOnOneTemplateType.EMPLOYEE_ONBOARDING_30_60_90 ]: "a2ff6670-8e2c-11ed-847d-acde48001122",
};

export const defaultSuggestedTemplateTypes = {
	[ TeamType.TEAM ]: [
		TeamTemplateType.REGULARLY_SCHEDULED_TEAM_MEETING,
		TeamTemplateType.DAILY_STANDUP,
		TeamTemplateType.SET_TEAM_GOALS,
	],
	[ TeamType.ONE_ON_ONE ]: [
		OneOnOneTemplateType.REGULAR_WEEKLY,
		OneOnOneTemplateType.GIVE_FEEDBACK,
		OneOnOneTemplateType.PROFESSIONAL_DEVELOPMENT,
	],
};

const cache = new Map();

export function clearCache() {
	cache.clear();
}

/**
 * Prepare a contextualization function to morphy Teaming template prototypes into
 *   template content that can be loaded into RTEs.
 * @param {Function} translate
 * @param {Array} templates - template prototypes that have not been contextualized
 * @param {Boolean} resetCache - should the global template cache be reset?
 * @returns {Function} contextualize
 * @NOTE this module was written for client-side templates and adapted to receive template
 *   prototypes that have been loaded from the API. It is probably not optimal.
 */
export function teamingTemplateFactory( { translate, templates = [], resetCache = false, } ) {
	if ( resetCache ) {
		clearCache();
	}

	const templatesById = templates.reduce( ( acc, template ) => {
		acc[ template.meetingTemplateId ] = template;
		return acc;
	}, {} );

	/**
	 * Resolve templates based on type
	 * @param templateType
	 * @returns { template:String, type:String, titleTranslationKey:String, descriptionTranslationKey:String }
	 */
	const resolveTemplate = templateType => {
		const meetingTemplateId = teamingTemplateIdMap[ templateType ];
		const templatePrototype = templatesById[ meetingTemplateId ];

		if ( TeamTemplateType.includes( templateType ) ) {
			return {
				meetingTemplateId,
				template: templatePrototype,
				type: templateType,
				scope: TeamType.TEAM,
			};
		}

		if ( OneOnOneTemplateType.includes( templateType ) ) {
			return {
				meetingTemplateId,
				template: templatePrototype,
				type: templateType,
				scope: TeamType.ONE_ON_ONE,
			};
		}

		return {
			meetingTemplateId: "",
			template: { name: "", description: "", },
			type: templateType,
			scope: "UNKNOWN",
		};
	};

	/**
	 * Fetch a template by type and contextualize it
	 * @param {Object} options
	 * @param {String} options.templateType
	 * @param {String} options.currentUserId
	 * @returns {Object} template
	 * @returns {String} template.title
	 * @returns {String} template.description
	 * @returns {Object} template.document
	 */
	return function contextualize( { templateType, currentUserId, currentUserName = "", refresh = false, } ) {
		const cacheKey = `${ templateType }:${ currentUserId }`;
		if ( cache.has( cacheKey ) && !refresh ) {
			return cache.get( cacheKey );
		}

		const {
			template,
		} = resolveTemplate( templateType );

		if ( !template ) {
			throw new Error( `no template exists for key ${ templateType }` );
		}

		const now = moment().utc().format();

		const actions = {};

		actions.text = node => {
			const textKey = node.text;
			node.text = translate( textKey );
		};

		actions[ "teaming-topic" ] = node => {
			node.attrs.id = uuidv4();
			node.attrs.creator = currentUserId;
			node.attrs[ "creator-name" ] = currentUserName;
			node.attrs[ "created-on" ] = now;
			node.attrs[ "is-reviewed" ] = "0";
			// TODO: are these needed?
			// node.attrs[ "assigned-to" ] = "";
			// node.attrs[ "completed-date" ] = "";
			// node.attrs[ "due-date" ] = "";
		};

		actions[ "teaming-commitment" ] = node => {
			node.attrs.id = uuidv4();
			node.attrs.creator = currentUserId;
			node.attrs[ "created-on" ] = now;
		};

		actions[ "teaming-decision" ] = node => {
			node.attrs.id = uuidv4();
			node.attrs.creator = currentUserId;
			node.attrs[ "created-on" ] = now;
		};

		const clonedTemplate = cloneDeep( template );
		let next = walk( clonedTemplate, [], actions );
		while ( next ) {
			// eslint-disable-next-line callback-return
			next = next();
		}

		const contextualizedTemplate = {
			...clonedTemplate,
			title: translate( template.name ),
			description: translate( template.description ) || "",
			type: templateType,
		};

		cache.set( cacheKey, contextualizedTemplate );

		return contextualizedTemplate;
	};
}

export function prepCustomTemplateContent( content ) {
	const _content = cloneDeep( content );
	return _content.map( x => {
		if ( x.type === "teaming-commitment" ) {
			delete x.attrs[ "assigned-to" ];
			delete x.attrs[ "assigned-by" ];
			delete x.attrs[ "completed-date" ];
			delete x.attrs[ "created-on" ];
			delete x.attrs.creator;
			delete x.attrs[ "due-date" ];
			delete x.attrs.id;
		}
		if ( x.type === "teaming-decision" ) {
			delete x.attrs[ "created-on" ];
			delete x.attrs.creator;
			delete x.attrs.id;
		}
		if ( x.type === "teaming-topic" ) {
			delete x.attrs[ "created-on" ];
			delete x.attrs.creator;
			delete x.attrs[ "creator-name" ];
			delete x.attrs.id;
			delete x.attrs[ "is-reviewed" ];
			delete x.attrs[ "suggested-topic-id" ];
		}
		return x;
	} );
}

const extractTemplateTopicText = ( head, tail = [], results = [] ) => {
	const { content = [], } = head;
	const firstChild = content[ 0 ] || {};
	const isTopic = node => node.type === "teaming-topic";

	if ( isTopic( head ) ) {
		results.push( firstChild.text );
	}
	if ( head.content ) {
		return () => {
			return extractTemplateTopicText( firstChild, [
				...content.slice( 1 ),
				...tail,
			], results );
		};
	}
	if ( !tail.length ) {
		return results;
	}
	return () => {
		return extractTemplateTopicText( tail[ 0 ], tail.slice( 1 ), results );
	};
};

/** @param {content:Array} template */
export function flattenTemplateTopics( template ) {
	let next = extractTemplateTopicText( template );
	// final results will be an array
	while ( !Array.isArray( next ) ) {
		// eslint-disable-next-line callback-return
		next = next();
	}
	return next;
}

const teamTemplateOrder = [
	TeamTemplateType.ALLSTATE_WEEKLY_TEAM_MEETING,
	TeamTemplateType.REGULARLY_SCHEDULED_TEAM_MEETING,
	TeamTemplateType.DAILY_STANDUP,
	TeamTemplateType.SET_TEAM_GOALS,
	TeamTemplateType.TEAM_RETROSPECTIVE,
];

const oneOnOneTemplateOrder = [
	OneOnOneTemplateType.REGULAR_WEEKLY,
	OneOnOneTemplateType.SOLVE_PROBLEMS,
	OneOnOneTemplateType.RESOLVE_CONFLICTS,
	OneOnOneTemplateType.GET_FEEDBACK,
	OneOnOneTemplateType.GIVE_FEEDBACK,
	OneOnOneTemplateType.REVIEW_PERFORMANCE,
	OneOnOneTemplateType.SET_GOALS,
	OneOnOneTemplateType.PROGRESS_CHECK_IN,
	OneOnOneTemplateType.RECOGNIZE_AND_APPRECIATE,
	OneOnOneTemplateType.ROLE_ALIGNMENT,
	OneOnOneTemplateType.ENGAGEMENT_CHECK_IN,
	OneOnOneTemplateType.HYBRID_WORK_CHECK_IN,
	OneOnOneTemplateType.REMOTE_WORK_CHECK_IN,
	OneOnOneTemplateType.PROFESSIONAL_DEVELOPMENT,
	OneOnOneTemplateType.SKIP_LEVEL_MEETING,
	OneOnOneTemplateType.FIRST_ONE_ON_ONE,
	OneOnOneTemplateType.QUARTERLY_PERFORMANCE_REVIEW,
	OneOnOneTemplateType.ANNUAL_PERFORMANCE_REVIEW,
	OneOnOneTemplateType.EMPLOYEE_ONBOARDING_30_60_90,
];

export function templateOrder( teamType = "" ) {
	if ( teamType === TeamType.TEAM ) {
		return [ ...teamTemplateOrder, ];
	}
	if ( teamType === TeamType.ONE_ON_ONE ) {
		return [ ...oneOnOneTemplateOrder, ];
	}
	return [
		...teamTemplateOrder,
		...oneOnOneTemplateOrder,
	];
}

const TEMPLATE_SELECTION_TIMESTAMP_FORMAT = "yyyy-LL-dd HH:mm:ss";

export const templateSelectionTimestamp = {
	FORMAT: TEMPLATE_SELECTION_TIMESTAMP_FORMAT,
	create: () => DateTime.utc().toFormat( TEMPLATE_SELECTION_TIMESTAMP_FORMAT ),
	parse: utcDateTime => DateTime.fromFormat( utcDateTime, TEMPLATE_SELECTION_TIMESTAMP_FORMAT, { zone: "UTC", } ),
};
